import {
  type FC,
  type FocusEventHandler,
  type MouseEventHandler,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'

import styles from './SummaryRow.module.css'

interface SummaryRowProps {
  label: string
  value: string
  bold?: boolean
  tooltip?: ReactNode
  tooltipClassName?: string
}

const TOOLTIP_OFFSET_PX = 8
const TOOLTIP_VIEWPORT_PADDING_PX = 8

const SummaryTooltip: FC<{
  information: ReactNode
  accessibleLabel: string
  className?: string
}> = ({ information, accessibleLabel, className }) => {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const closeTimeoutRef = useRef<number | null>(null)
  const tooltipId = useId()
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current != null) {
      window.clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }

  const openTooltip = () => {
    clearCloseTimeout()
    setOpen(true)
  }

  const closeTooltip = () => {
    clearCloseTimeout()
    closeTimeoutRef.current = window.setTimeout(() => {
      setOpen(false)
    }, 80)
  }

  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault()
    event.stopPropagation()
    clearCloseTimeout()
    setOpen((current) => !current)
  }

  const handleBlur: FocusEventHandler<HTMLButtonElement> = () => {
    closeTooltip()
  }

  const updatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return

    const triggerRect = triggerRef.current.getBoundingClientRect()
    const tooltipRect = tooltipRef.current.getBoundingClientRect()
    const centeredLeft = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2
    const left = Math.max(
      TOOLTIP_VIEWPORT_PADDING_PX,
      Math.min(centeredLeft, window.innerWidth - tooltipRect.width - TOOLTIP_VIEWPORT_PADDING_PX)
    )
    const top = Math.max(
      TOOLTIP_VIEWPORT_PADDING_PX,
      Math.min(
        triggerRect.bottom + TOOLTIP_OFFSET_PX,
        window.innerHeight - tooltipRect.height - TOOLTIP_VIEWPORT_PADDING_PX
      )
    )

    setPosition({ top, left })
  }

  useEffect(() => () => clearCloseTimeout(), [])

  useLayoutEffect(() => {
    if (!open) return

    updatePosition()

    const handleViewportChange = () => updatePosition()

    window.addEventListener('resize', handleViewportChange)
    window.addEventListener('scroll', handleViewportChange, true)

    return () => {
      window.removeEventListener('resize', handleViewportChange)
      window.removeEventListener('scroll', handleViewportChange, true)
    }
  }, [open])

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={styles.tooltipTrigger}
        aria-label={accessibleLabel}
        aria-describedby={open ? tooltipId : undefined}
        onMouseEnter={openTooltip}
        onMouseLeave={closeTooltip}
        onFocus={openTooltip}
        onBlur={handleBlur}
        onClick={handleClick}
      >
        <Icon.Small icon="info" encircled="grey800" />
      </button>
      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            id={tooltipId}
            ref={tooltipRef}
            role="tooltip"
            data-testid="tooltip"
            className={[styles.tooltip, className].filter(Boolean).join(' ')}
            style={{ top: position.top, left: position.left }}
            onMouseEnter={openTooltip}
            onMouseLeave={closeTooltip}
          >
            {information}
          </div>,
          document.body
        )}
    </>
  )
}

export const SummaryRow: FC<SummaryRowProps> = ({
  label,
  value,
  bold,
  tooltip,
  tooltipClassName,
}) => (
  <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
    <Box flexDirection="row" alignItems="center" gap="s6">
      {tooltip && (
        <SummaryTooltip
          information={tooltip}
          accessibleLabel={label}
          className={tooltipClassName}
        />
      )}
      <Text size="200" color="grey700">
        {label}
      </Text>
    </Box>
    <Text size="200" weight={bold ? 'semibold' : 'normal'} color={bold ? 'grey1000' : 'grey700'}>
      {value}
    </Text>
  </Box>
)
